// Ingredient science education - evidence-based information

export interface IngredientInfo {
  id: string;
  name: string;
  category: 'active' | 'hydrator' | 'antioxidant' | 'soothing' | 'exfoliant' | 'barrier-support';
  benefits: string[];
  scientificExplanation: string;
  bestFor: string[];
  concentration: string;
  notes?: string;
}

export const ingredientLibrary: IngredientInfo[] = [
  {
    id: 'niacinamide',
    name: 'Niacinamide (Vitamin B3)',
    category: 'active',
    benefits: [
      'Reduces sebum production',
      'Minimizes pore appearance',
      'Brightens hyperpigmentation',
      'Strengthens barrier function',
      'Anti-inflammatory'
    ],
    scientificExplanation: 'Niacinamide inhibits melanosome transfer to keratinocytes, reducing hyperpigmentation. It regulates sebum through interaction with sebocyte differentiation. Acts as a precursor to NAD+ and NADP+, supporting cellular energy and barrier lipid synthesis.',
    bestFor: ['Oily skin', 'Post-acne marks', 'Large pores', 'Uneven tone'],
    concentration: 'Effective at 2-10%. Higher concentrations (10%+) may cause flushing in sensitive individuals.'
  },
  {
    id: 'hyaluronic-acid',
    name: 'Hyaluronic Acid',
    category: 'hydrator',
    benefits: [
      'Binds 1000x its weight in water',
      'Plumps skin surface',
      'Supports barrier hydration',
      'Non-comedogenic'
    ],
    scientificExplanation: 'A glycosaminoglycan that attracts and retains water molecules in the extracellular matrix. Multiple molecular weights hydrate different skin depths: high MW on surface, low MW in deeper layers.',
    bestFor: ['All skin types', 'Dehydrated skin', 'Acne-prone (gel formulations)'],
    concentration: 'Effective at 0.1-2%. Often combined with other humectants.',
    notes: 'Apply to damp skin and seal with occlusives in dry climates.'
  },
  {
    id: 'centella-asiatica',
    name: 'Centella Asiatica (Cica)',
    category: 'soothing',
    benefits: [
      'Promotes wound healing',
      'Anti-inflammatory',
      'Stimulates collagen synthesis',
      'Calms irritation'
    ],
    scientificExplanation: 'Contains triterpenoids (asiaticoside, madecassoside, asiatic acid, madecassic acid) that promote fibroblast proliferation and collagen synthesis. Reduces inflammatory cytokines and supports barrier repair.',
    bestFor: ['Sensitive skin', 'Damaged barrier', 'Post-procedure', 'Inflamed acne'],
    concentration: 'Effective at 0.1-1% extract or higher percentages of full plant extract.'
  },
  {
    id: 'salicylic-acid',
    name: 'Salicylic Acid (BHA)',
    category: 'exfoliant',
    benefits: [
      'Penetrates pores',
      'Dissolves sebum and debris',
      'Anti-inflammatory',
      'Prevents comedone formation'
    ],
    scientificExplanation: 'Lipophilic beta hydroxy acid that penetrates sebaceous follicles. Disrupts corneocyte cohesion and reduces P. acnes proliferation. Anti-inflammatory through COX inhibition (aspirin derivative).',
    bestFor: ['Oily skin', 'Blackheads', 'Whiteheads', 'Acne-prone skin'],
    concentration: 'OTC: 0.5-2%. Prescription: up to 10%. Start low (0.5-1%) for tolerance.',
    notes: 'Use PM only initially. Increases sun sensitivity—wear SPF.'
  },
  {
    id: 'azelaic-acid',
    name: 'Azelaic Acid',
    category: 'active',
    benefits: [
      'Inhibits tyrosinase (brightening)',
      'Antibacterial against P. acnes',
      'Anti-inflammatory',
      'Normalizes keratinization'
    ],
    scientificExplanation: 'Dicarboxylic acid that competitively inhibits tyrosinase, reducing melanin synthesis. Antibacterial through disruption of bacterial protein synthesis. Reduces abnormal keratinocyte proliferation.',
    bestFor: ['Post-acne marks', 'Rosacea', 'Melasma', 'Active acne'],
    concentration: 'OTC: 10%. Prescription: 15-20%. Generally well-tolerated.',
    notes: 'May cause temporary tingling. Less irritating than retinoids or AHAs.'
  },
  {
    id: 'snail-mucin',
    name: 'Snail Secretion Filtrate',
    category: 'hydrator',
    benefits: [
      'Provides glycoproteins',
      'Supports wound healing',
      'Hydrates and plumps',
      'Anti-inflammatory'
    ],
    scientificExplanation: 'Contains allantoin, glycolic acid, hyaluronic acid, glycoprotein enzymes, and antimicrobial peptides. Promotes fibroblast proliferation and extracellular matrix remodeling.',
    bestFor: ['Damaged barrier', 'Acne scars', 'Dehydrated skin', 'Sensitive skin'],
    concentration: 'Effective at 90-96% in essences. Lower percentages in creams.',
    notes: 'Cruelty-free harvesting methods exist. Vegan alternatives: peptides + HA.'
  },
  {
    id: 'ceramides',
    name: 'Ceramides',
    category: 'barrier-support',
    benefits: [
      'Replenish barrier lipids',
      'Prevent transepidermal water loss',
      'Support SC integrity',
      'Reduce sensitivity'
    ],
    scientificExplanation: 'Lipid molecules that comprise 50% of stratum corneum composition. Fill intercellular spaces between corneocytes. Essential for barrier function and water retention.',
    bestFor: ['Damaged barrier', 'Dry skin', 'Eczema', 'Post-acne healing'],
    concentration: 'Effective in multi-lamellar formulations. Often combined with cholesterol and fatty acids.',
    notes: 'Most effective when formulated in physiological ratios (ceramide:cholesterol:fatty acids = 1:1:1).'
  },
  {
    id: 'vitamin-c',
    name: 'Ascorbic Acid (Vitamin C)',
    category: 'antioxidant',
    benefits: [
      'Neutralizes free radicals',
      'Inhibits tyrosinase',
      'Promotes collagen synthesis',
      'Photoprotection (adjunct)'
    ],
    scientificExplanation: 'Water-soluble antioxidant that donates electrons to neutralize ROS. Co-factor for prolyl hydroxylase (collagen synthesis). Reduces melanin formation through tyrosinase inhibition.',
    bestFor: ['Hyperpigmentation', 'Photoaging', 'Dullness', 'Antioxidant protection'],
    concentration: 'Effective at 10-20% L-ascorbic acid. Derivatives (MAP, SAP) at 5-10%.',
    notes: 'Requires pH < 3.5 for penetration. Store in dark, airtight packaging. Use with vitamin E and ferulic acid for stability.'
  },
  {
    id: 'retinol',
    name: 'Retinol (Vitamin A)',
    category: 'active',
    benefits: [
      'Increases cell turnover',
      'Stimulates collagen',
      'Normalizes keratinization',
      'Reduces comedones'
    ],
    scientificExplanation: 'Converted to retinoic acid in skin. Binds to nuclear retinoic acid receptors (RARs) to regulate gene transcription. Increases epidermal thickness and dermal collagen.',
    bestFor: ['Acne', 'Photoaging', 'Texture', 'Post-acne marks'],
    concentration: 'Start: 0.01-0.025%. Work up to 0.1-1%. Prescription tretinoin: 0.025-0.1%.',
    notes: 'Not for pregnancy/nursing. Causes retinization (peeling/redness) initially. Use PM only with SPF.'
  },
  {
    id: 'panthenol',
    name: 'Panthenol (Pro-Vitamin B5)',
    category: 'soothing',
    benefits: [
      'Deeply moisturizing',
      'Anti-inflammatory',
      'Supports barrier repair',
      'Wound healing'
    ],
    scientificExplanation: 'Converted to pantothenic acid in skin. Essential for lipid synthesis and cellular metabolism. Stimulates fibroblast proliferation and accelerates re-epithelialization.',
    bestFor: ['All skin types', 'Damaged barrier', 'Irritation', 'Post-procedure'],
    concentration: 'Effective at 1-5%. Higher concentrations in ointments.',
    notes: 'Excellent for post-acid or post-laser recovery.'
  },
  {
    id: 'glycolic-acid',
    name: 'Glycolic Acid (AHA)',
    category: 'exfoliant',
    benefits: [
      'Exfoliates surface cells',
      'Improves texture',
      'Brightens tone',
      'Stimulates collagen'
    ],
    scientificExplanation: 'Smallest alpha hydroxy acid with excellent penetration. Weakens corneocyte adhesion by breaking desmosomes. Stimulates glycosaminoglycan synthesis in dermis.',
    bestFor: ['Texture', 'Dullness', 'Fine lines', 'Sun damage'],
    concentration: 'OTC: 5-10%. Professional: 20-70%. pH matters for efficacy.',
    notes: 'Increases photosensitivity. Use PM with SPF. May irritate sensitive/rosacea-prone skin.'
  },
  {
    id: 'green-tea',
    name: 'Green Tea Extract (EGCG)',
    category: 'antioxidant',
    benefits: [
      'Potent antioxidant',
      'Anti-inflammatory',
      'Regulates sebum',
      'Antimicrobial'
    ],
    scientificExplanation: 'Polyphenols (epigallocatechin gallate) scavenge free radicals and inhibit NF-κB signaling. Reduces sebum oxidation and P. acnes growth through antimicrobial peptides.',
    bestFor: ['Oily skin', 'Acne-prone', 'Redness', 'Photoprotection'],
    concentration: 'Effective at 1-3% extract (standardized to EGCG content).',
    notes: 'Synergistic with vitamin C and E. Store away from light.'
  },
  {
    id: 'zinc',
    name: 'Zinc Oxide / Zinc PCA',
    category: 'active',
    benefits: [
      'Sebum regulation',
      'Anti-inflammatory',
      'Antimicrobial',
      'Wound healing'
    ],
    scientificExplanation: 'Essential mineral that regulates sebaceous gland activity and supports wound healing. Zinc oxide provides physical UV protection. Zinc PCA controls sebum and reduces inflammation.',
    bestFor: ['Oily skin', 'Acne', 'Sun protection', 'Healing'],
    concentration: 'Zinc PCA: 0.5-1%. Zinc oxide (sunscreen): 10-25%.',
    notes: 'Oral zinc supplementation (25-30mg) may help moderate-severe acne.'
  },
  {
    id: 'tea-tree-oil',
    name: 'Tea Tree Oil',
    category: 'active',
    benefits: [
      'Antimicrobial against P. acnes',
      'Anti-inflammatory',
      'Reduces lesion count',
      'Natural alternative'
    ],
    scientificExplanation: 'Terpinen-4-ol disrupts bacterial cell membranes. Reduces inflammatory cytokines (IL-1α, IL-8, IL-10). Studies show 5% tea tree oil comparable to 5% benzoyl peroxide for acne.',
    bestFor: ['Mild-moderate acne', 'Spot treatment', 'Natural routines'],
    concentration: '5% for acne treatment. Must dilute pure oil (toxic if ingested).',
    notes: 'Patch test first. Can cause irritation in sensitive individuals. Takes 4-12 weeks for results.'
  },
  {
    id: 'mugwort',
    name: 'Mugwort (Artemisia)',
    category: 'soothing',
    benefits: [
      'Anti-inflammatory',
      'Calms irritation',
      'Antioxidant',
      'Soothing'
    ],
    scientificExplanation: 'Contains azulene, flavonoids, and tannins with anti-inflammatory properties. Inhibits histamine release and reduces redness. Traditional use in Asian medicine for sensitive skin.',
    bestFor: ['Sensitive skin', 'Redness', 'Irritation', 'Reactive skin'],
    concentration: 'Effective at various percentages depending on extract concentration.',
    notes: 'Well-tolerated. Can be used daily. Often combined with centella.'
  }
];

export const getIngredientByName = (name: string): IngredientInfo | undefined => {
  // Normalize the input name - remove percentages, parentheses, and extra whitespace
  const normalizedInput = name.toLowerCase()
    .replace(/\([^)]*\)/g, '') // Remove parentheses and content
    .replace(/\d+%/g, '') // Remove percentages
    .trim();
  
  return ingredientLibrary.find(
    ing => {
      const normalizedIngName = ing.name.toLowerCase();
      const normalizedIngId = ing.id.toLowerCase();
      
      // Check if input contains ingredient name or vice versa
      return normalizedIngName.includes(normalizedInput) || 
             normalizedInput.includes(normalizedIngName) ||
             normalizedIngId === normalizedInput ||
             normalizedInput.includes(normalizedIngId) ||
             // Handle common aliases
             (normalizedInput.includes('snail') && normalizedIngName.includes('snail')) ||
             (normalizedInput.includes('mucin') && normalizedIngName.includes('snail')) ||
             (normalizedInput.includes('bha') && normalizedIngName.includes('salicylic')) ||
             (normalizedInput.includes('aha') && (normalizedIngName.includes('glycolic') || normalizedIngName.includes('lactic'))) ||
             (normalizedInput.includes('pha') && normalizedIngName.includes('polyhydroxy')) ||
             (normalizedInput.includes('vitamin c') && normalizedIngName.includes('ascorbic')) ||
             (normalizedInput.includes('vitamin b3') && normalizedIngName.includes('niacinamide')) ||
             (normalizedInput.includes('panthenol') && normalizedIngName.includes('panthenol')) ||
             (normalizedInput.includes('centella') && normalizedIngName.includes('centella')) ||
             (normalizedInput.includes('cica') && normalizedIngName.includes('centella')) ||
             (normalizedInput.includes('hyaluronic') && normalizedIngName.includes('hyaluronic')) ||
             (normalizedInput.includes('ceramide') && normalizedIngName.includes('ceramide')) ||
             (normalizedInput.includes('niacinamide') && normalizedIngName.includes('niacinamide')) ||
             (normalizedInput.includes('azelaic') && normalizedIngName.includes('azelaic')) ||
             (normalizedInput.includes('arbutin') && normalizedIngName.includes('arbutin')) ||
             (normalizedInput.includes('zinc') && normalizedIngName.includes('zinc')) ||
             (normalizedInput.includes('tea tree') && normalizedIngName.includes('tea tree')) ||
             (normalizedInput.includes('green tea') && normalizedIngName.includes('green tea'));
    }
  );
};

export const getIngredientsByCategory = (category: IngredientInfo['category']): IngredientInfo[] => {
  return ingredientLibrary.filter(ing => ing.category === category);
};
